import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from './useApiClient';

export type EnvironmentType = 'aws' | 'terraform' | 'gcp' | 'azure';

export interface Environment {
  id: string;
  tenantId: string;
  name: string;
  type: EnvironmentType;
  description?: string;
  rules?: string;
  specs?: string;
  gitConnectionIds?: string[];
  awsConnectionId?: string;
  agentRoleArn?: string;
  status: 'active' | 'inactive';
  createdAt: string;
  updatedAt?: string;
  createdBy: string;
}

export interface CreateEnvironmentRequest {
  name: string;
  type: EnvironmentType;
  description?: string;
  rules?: string;
  specs?: string;
  gitConnectionIds?: string[];
  awsConnectionId?: string;
}

export interface UpdateEnvironmentRequest {
  name: string;
  description?: string;
  rules?: string;
  specs?: string;
  gitConnectionIds?: string[];
  awsConnectionId?: string;
  status?: 'active' | 'inactive';
}

export const useEnvironments = (tenantId: string) => {
  const apiClient = useApiClient();
  
  return useQuery({
    queryKey: ['environments', tenantId],
    queryFn: async (): Promise<Environment[]> => {
      const response = await apiClient.get(`/tenants/${tenantId}/environments`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch environments');
      }
      
      const data = await response.json();
      return data.environments || [];
    },
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useEnvironment = (tenantId: string, environmentId: string) => {
  const apiClient = useApiClient();
  
  return useQuery({
    queryKey: ['environment', tenantId, environmentId],
    queryFn: async (): Promise<Environment> => {
      const response = await apiClient.get(`/tenants/${tenantId}/environments/${environmentId}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch environment');
      }
      
      return response.json();
    },
    enabled: !!tenantId && !!environmentId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateEnvironment = () => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ tenantId, data }: { tenantId: string; data: CreateEnvironmentRequest }) => {
      const response = await apiClient.post(`/tenants/${tenantId}/environments`, data);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create environment');
      }
      
      return response.json();
    },
    onSuccess: (_, { tenantId }) => {
      queryClient.invalidateQueries({ queryKey: ['environments', tenantId] });
    },
  });
};

export const useUpdateEnvironment = () => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ tenantId, environmentId, data }: { 
      tenantId: string; 
      environmentId: string; 
      data: UpdateEnvironmentRequest 
    }) => {
      const response = await apiClient.put(`/tenants/${tenantId}/environments/${environmentId}`, data);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update environment');
      }
      
      return response.json();
    },
    onSuccess: (_, { tenantId, environmentId }) => {
      queryClient.invalidateQueries({ queryKey: ['environments', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['environment', tenantId, environmentId] });
    },
  });
};

export const useDeleteEnvironment = () => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ tenantId, environmentId }: { tenantId: string; environmentId: string }) => {
      const response = await apiClient.delete(`/tenants/${tenantId}/environments/${environmentId}`);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete environment');
      }
    },
    onSuccess: (_, { tenantId }) => {
      queryClient.invalidateQueries({ queryKey: ['environments', tenantId] });
    },
  });
};